import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { mapProfile, formatHeight, formatWeight } from "@/lib/adapt";
import { useSaved } from "@/context/SavedContext";
import { useAuth } from "@/context/AuthContext";
import { useCelebrate } from "@/context/MatchCelebration";
import { EtiquetteNote } from "@/components/EtiquetteNote";
import { CompatibilityBreakdown } from "@/components/CompatibilityBreakdown";
import { RequestContactDialog } from "@/components/RequestContactDialog";
import { CompareDialog } from "@/components/CompareDialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Heart, X, MapPin, Briefcase, GraduationCap, ShieldCheck, Lock, Crown, ArrowLeft, Mail, Phone, Flag, Share2, Plane, Languages as LangIcon, Ruler, Check, BadgeCheck, ChevronLeft, ChevronRight, Bookmark, BookmarkCheck, Sparkles, Maximize2, Send, Clock, ArrowLeftRight } from "lucide-react";

export default function ProfileDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const celebrate = useCelebrate();
  const { user } = useAuth();
  const { isSaved, toggle } = useSaved();
  const [data, setData] = useState(null);
  const [active, setActive] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const [reportReason, setReportReason] = useState("");
  const [reportDetails, setReportDetails] = useState("");
  const [busy, setBusy] = useState(false);
  const [reqStatus, setReqStatus] = useState(null);
  const [reqOpen, setReqOpen] = useState(false);
  const [compareOpen, setCompareOpen] = useState(false);
  const isAssistant = !!user?.is_assistant;

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/profile/${id}`); setData(mapProfile(data.user || data)); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); navigate("/discovery"); }
  }, [id, navigate]);

  const loadReqStatus = useCallback(async () => {
    if (!isAssistant) return;
    try {
      const { data } = await api.get("/assist/my-contact-requests");
      const m = (data.requests || []).find((r) => String(r.profile?.id ?? r.targetId) === String(id));
      setReqStatus(m ? { status: m.status, requestId: m.requestId } : { status: "none" });
    } catch { setReqStatus({ status: "none" }); }
  }, [isAssistant, id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadReqStatus(); }, [loadReqStatus]);

  const decide = async (action) => {
    if (isOwnProfile) { toast.info("You can't show interest on your own profile"); return; }
    setBusy(true);
    try {
      const { data: r } = await api.post("/actions", { targetId: id, action });
      if (r.matched) celebrate(data);
      else toast.success(action === "interested" ? "Interest sent" : "Marked as no interest");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const onSave = async () => {
    try { const now = await toggle?.(id); toast.success(now ? "Saved to your list" : "Removed from your saved list"); } catch { toast.error("Couldn't update saved list"); }
  };

  const submitReport = async () => {
    if (!reportReason) return toast.error("Please select a reason");
    try { await api.post("/report", { targetId: id, reason: reportReason, details: reportDetails }); toast.success("Report submitted. Our team will review it."); setReportReason(""); setReportDetails(""); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const share = async () => {
    const url = window.location.href;
    try { if (navigator.share) await navigator.share({ title: data.name, url }); else { await navigator.clipboard.writeText(url); toast.success("Profile link copied"); } } catch {}
  };

  if (!data) return <div className="grid gap-8 lg:grid-cols-2"><Skeleton className="aspect-[4/5] rounded-3xl" /><div className="space-y-4"><Skeleton className="h-10 w-2/3" /><Skeleton className="h-40" /></div></div>;

  const photos = data.photos || [];
  const isOwnProfile = !!user?.id && (String(user.id) === String(data.id) || String(user.id) === String(id));
  const current = photos[active];
  const saved = isSaved ? isSaved(id) : false;
  const hasContact = data.contact && (data.contact.email || data.contact.phone || data.contact.whatsapp || data.wali?.name);
  const go = (dir) => setActive((i) => (photos.length ? (i + dir + photos.length) % photos.length : 0));

  return (
    <div>
      <Button variant="ghost" onClick={() => navigate(-1)} data-testid="back-btn" className="mb-4 text-emerald-800"><ArrowLeft className="mr-2 h-4 w-4" /> Back</Button>
      <div className="grid gap-8 lg:grid-cols-[minmax(0,460px)_1fr]" data-testid="profile-detail">
        {/* Photo carousel */}
        <div className="lg:sticky lg:top-24 lg:self-start">
          <div className="relative aspect-[4/5] overflow-hidden rounded-3xl border border-emerald-900/10 bg-emerald-900 shadow-sm">
            {current ? (
              <img src={current.src} alt={data.name} className="h-full w-full cursor-zoom-in object-cover" data-testid="detail-main-photo" onClick={() => setLightbox(true)} />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-7xl font-serif text-amber-300/50">{data.name?.[0]}</div>
            )}

            {typeof data.match_percent === "number" && (
              <div className="glass absolute left-4 top-4 flex items-center gap-1.5 rounded-full bg-emerald-950/55 px-3 py-1.5 text-xs font-bold text-white"><Sparkles className="h-3.5 w-3.5 text-amber-300" /> {data.match_percent}% Match</div>
            )}
            <button onClick={onSave} data-testid="detail-save-btn" aria-label="Save" className={`glass absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full shadow-sm transition-colors ${saved ? "bg-amber-500 text-white" : "bg-white/85 text-emerald-800 hover:bg-white"}`}>{saved ? <BookmarkCheck className="h-5 w-5" /> : <Bookmark className="h-5 w-5" />}</button>

            {photos.length > 1 && (
              <>
                <button onClick={() => go(-1)} data-testid="carousel-prev" className="glass absolute left-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/80 text-emerald-900 shadow-sm hover:bg-white"><ChevronLeft className="h-5 w-5" /></button>
                <button onClick={() => go(1)} data-testid="carousel-next" className="glass absolute right-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/80 text-emerald-900 shadow-sm hover:bg-white"><ChevronRight className="h-5 w-5" /></button>
                <div className="absolute inset-x-0 bottom-4 flex justify-center gap-1.5">
                  {photos.map((_, i) => <button key={i} onClick={() => setActive(i)} data-testid={`carousel-dot-${i}`} className={`h-2 rounded-full transition-all ${i === active ? "w-6 bg-white" : "w-2 bg-white/50"}`} />)}
                </div>
              </>
            )}
            <button onClick={() => setLightbox(true)} className="glass absolute bottom-4 right-4 flex h-9 w-9 items-center justify-center rounded-full bg-white/80 text-emerald-900 hover:bg-white" data-testid="carousel-expand"><Maximize2 className="h-4 w-4" /></button>
          </div>
          {photos.length > 1 && (
            <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
              {photos.map((ph, i) => <button key={ph.id} onClick={() => setActive(i)} className={`h-16 w-16 shrink-0 overflow-hidden rounded-xl border-2 ${i === active ? "border-amber-500" : "border-transparent opacity-70"}`}><img src={ph.src} alt="" className="h-full w-full object-cover" /></button>)}
            </div>
          )}
        </div>

        {/* Content cards */}
        <div className="space-y-5">
          <div className="rounded-3xl border border-emerald-900/10 bg-white p-6 shadow-sm">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h1 className="font-serif text-4xl font-bold text-emerald-950">{data.name}{data.age ? `, ${data.age}` : ""}</h1>
                <p className="mt-1 flex items-center gap-1.5 text-slate-600"><MapPin className="h-4 w-4" /> {[data.city, data.country].filter(Boolean).join(", ") || "—"}</p>
              </div>
              <div className="flex gap-2">
                <button onClick={share} data-testid="share-btn" className="rounded-full border border-emerald-900/10 p-2.5 text-emerald-800 hover:bg-mint"><Share2 className="h-4 w-4" /></button>
                <Dialog>
                  <DialogTrigger asChild><button data-testid="report-btn" className="rounded-full border border-red-200 p-2.5 text-red-600 hover:bg-red-50"><Flag className="h-4 w-4" /></button></DialogTrigger>
                  <DialogContent>
                    <DialogHeader><DialogTitle className="font-serif">Report this profile</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                      <Select value={reportReason} onValueChange={setReportReason}>
                        <SelectTrigger data-testid="report-reason"><SelectValue placeholder="Select a reason" /></SelectTrigger>
                        <SelectContent>{["Fake profile", "Inappropriate content", "Harassment", "Scam / spam", "Other"].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                      </Select>
                      <Textarea value={reportDetails} onChange={(e) => setReportDetails(e.target.value)} placeholder="Additional details (optional)" data-testid="report-details" />
                    </div>
                    <DialogFooter><Button onClick={submitReport} data-testid="report-submit" className="bg-red-600 text-white hover:bg-red-700">Submit report</Button></DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {data.verified && <Badge className="gap-1 bg-emerald-600 text-white hover:bg-emerald-600"><BadgeCheck className="h-3 w-3" /> Verified</Badge>}
              {data.verified_wali && <Badge className="gap-1 bg-indigo-600 text-white hover:bg-indigo-600"><ShieldCheck className="h-3 w-3" /> Wali</Badge>}
              {data.is_premium && <Badge className="gap-1 bg-amber-500 text-white hover:bg-amber-500"><Crown className="h-3 w-3" /> Premium</Badge>}
            </div>
            {data.bio && <p className="mt-5 leading-relaxed text-slate-700" data-testid="detail-bio">{data.bio}</p>}
          </div>

          <div className="rounded-3xl border border-emerald-900/10 bg-white p-6 shadow-sm">
            <h3 className="mb-4 font-serif text-xl font-semibold text-emerald-900">About {data.firstName || data.name}</h3>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              <Stat icon={Briefcase} label="Profession" value={data.profession} />
              <Stat icon={GraduationCap} label="Education" value={data.education} />
              <Stat icon={Ruler} label="Height" value={formatHeight(data.height_cm, data.heightUnit)} />
              <Stat icon={Ruler} label="Weight" value={formatWeight(data.weight, data.weightUnit)} />
              <Stat icon={ShieldCheck} label="Sect" value={data.sect} />
              <Stat icon={Heart} label="Prayer" value={data.prayer_habit} />
              <Stat icon={Plane} label="Relocation" value={data.relocation ? "Open" : "Not now"} />
              <Stat icon={LangIcon} label="Languages" value={(data.languages || []).join(", ")} />
              <Stat icon={Check} label="Marital" value={data.marital_status} />
            </div>
            {(data.interests || []).length > 0 && (
              <div className="mt-6">
                <p className="mb-2 text-xs font-semibold uppercase tracking-[0.2em] text-amber-700">Interests</p>
                <div className="flex flex-wrap gap-2">{data.interests.map((i) => <Badge key={i} variant="secondary" className="bg-mint text-emerald-800">{i}</Badge>)}</div>
              </div>
            )}
          </div>

          {!isAssistant && <CompatibilityBreakdown me={user?.profile} them={data} />}

          <div className="rounded-3xl border border-emerald-900/10 bg-white p-6 shadow-sm">
            {hasContact ? (
              <div data-testid="contact-info">
                <p className="mb-3 flex items-center gap-2 font-serif text-lg font-semibold text-emerald-900"><Check className="h-5 w-5 text-emerald-600" /> Contact details</p>
                {data.contact.email && <p className="flex items-center gap-2 text-slate-700"><Mail className="h-4 w-4 text-emerald-700" /> {data.contact.email}</p>}
                {data.contact.phone && <p className="mt-1 flex items-center gap-2 text-slate-700"><Phone className="h-4 w-4 text-emerald-700" /> {data.contact.phone}</p>}
                <div className="mt-4"><EtiquetteNote wali={data.wali} /></div>
              </div>
            ) : isAssistant ? (
              <AssistantContactState status={reqStatus?.status} isPremium={!!user?.is_premium} name={data.firstName || data.name} onRequest={() => setReqOpen(true)} onUpgrade={() => navigate("/premium")} />
            ) : (
              <div>
                <div className="flex items-center gap-3 text-slate-600" data-testid="contact-locked"><Lock className="h-5 w-5 text-amber-600" /><p>Contact details unlock after a mutual match and the member chooses to share them.</p></div>
                <div className="mt-4"><EtiquetteNote wali={data.wali} /></div>
              </div>
            )}
          </div>

          {isAssistant ? (
            (reqStatus?.status === "none" || reqStatus?.status === "declined") && (
              <div className="sticky bottom-20 z-10 flex gap-3 rounded-3xl border border-emerald-900/10 bg-white/90 p-3 shadow-lg glass lg:bottom-4">
                <Button onClick={() => setReqOpen(true)} data-testid="detail-request-contact-btn" className="flex-1 rounded-xl bg-emerald-800 py-6 text-white hover:bg-emerald-900"><Send className="mr-2 h-5 w-5" /> Request Contact</Button>
              </div>
            )
          ) : (
            <>
              <div className="sticky bottom-20 z-10 flex gap-3 rounded-3xl border border-emerald-900/10 bg-white/90 p-3 shadow-lg glass lg:bottom-4">
                <Button variant="outline" disabled={busy} onClick={() => decide("not_interested")} data-testid="detail-pass-btn" className={`flex-1 rounded-xl border-red-200 py-6 text-red-600 hover:bg-red-50 ${isOwnProfile ? "opacity-50" : ""}`}><X className="mr-2 h-5 w-5" /> No Interest</Button>
                <Button disabled={busy} onClick={() => decide("interested")} data-testid="detail-interested-btn" className={`flex-1 rounded-xl bg-emerald-800 py-6 text-white hover:bg-emerald-900 ${isOwnProfile ? "opacity-50" : ""}`}><Heart className="mr-2 h-5 w-5 fill-current" /> Interested</Button>
              </div>
              {!isOwnProfile && <Button variant="outline" onClick={() => setCompareOpen(true)} data-testid="detail-compare-btn" className="w-full rounded-xl border-amber-400 bg-amber-50 py-6 text-amber-800 hover:bg-amber-100"><ArrowLeftRight className="mr-2 h-5 w-5" /> Compare Profiles</Button>}
              {isOwnProfile && <p className="text-center text-sm text-slate-500" data-testid="own-profile-note">This is your own profile — you can't show interest on yourself.</p>}
            </>
          )}
        </div>
      </div>

      {!isAssistant && !isOwnProfile && <CompareDialog open={compareOpen} onOpenChange={setCompareOpen} me={user?.profile} them={data} />}
      <RequestContactDialog open={reqOpen} onOpenChange={setReqOpen} targetId={id} targetName={data.name} onDone={loadReqStatus} />

      {/* Lightbox */}
      <Dialog open={lightbox} onOpenChange={setLightbox}>
        <DialogContent className="max-w-3xl border-0 bg-transparent p-0 shadow-none" data-testid="photo-lightbox">
          <DialogTitle className="sr-only">Photo</DialogTitle>
          {current && <img src={current.src} alt={data.name} className="max-h-[85vh] w-full rounded-2xl object-contain" />}
          {photos.length > 1 && (
            <div className="mt-3 flex justify-center gap-2">
              <button onClick={() => go(-1)} className="rounded-full bg-white/90 p-2 text-emerald-900"><ChevronLeft className="h-5 w-5" /></button>
              <button onClick={() => go(1)} className="rounded-full bg-white/90 p-2 text-emerald-900"><ChevronRight className="h-5 w-5" /></button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="rounded-xl border border-emerald-900/10 bg-mint/40 p-3">
      <p className="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-emerald-700"><Icon className="h-3.5 w-3.5" /> {label}</p>
      <p className="mt-0.5 text-sm font-medium text-slate-800">{value}</p>
    </div>
  );
}

function AssistantContactState({ status, isPremium, name, onRequest, onUpgrade }) {
  if (status === "approved") {
    if (!isPremium) {
      return (
        <div data-testid="assist-contact-approved-nonpremium">
          <div className="flex items-center gap-3 text-emerald-800"><Crown className="h-5 w-5 text-amber-500" /><p className="font-medium">Approved — upgrade to Premium to view contact details.</p></div>
          <Button onClick={onUpgrade} data-testid="assist-upgrade-btn" className="mt-4 rounded-full bg-amber-500 text-white hover:bg-amber-600"><Crown className="mr-1.5 h-4 w-4" /> Upgrade to Premium</Button>
        </div>
      );
    }
    return <div className="flex items-center gap-3 text-slate-600" data-testid="assist-contact-approved-premium"><Lock className="h-5 w-5 text-amber-600" /><p>Approved. Contact details will appear here shortly.</p></div>;
  }
  if (status === "pending") {
    return (
      <div data-testid="assist-contact-pending">
        <div className="flex items-center gap-3 text-slate-600"><Clock className="h-5 w-5 text-amber-600" /><p>Your contact request is pending {name}'s approval.</p></div>
        {!isPremium && <p className="mt-2 text-sm text-slate-500">Viewing contact details also requires an active Premium subscription once they approve.</p>}
      </div>
    );
  }
  if (status === "declined") {
    return <div className="flex items-center gap-3 text-slate-600" data-testid="assist-contact-declined"><X className="h-5 w-5 text-red-500" /><p>Your contact request was declined by the member.</p></div>;
  }
  return (
    <div data-testid="assist-contact-none">
      <div className="flex items-center gap-3 text-slate-600"><Lock className="h-5 w-5 text-amber-600" /><p>Send a contact request. Viewing details requires Premium and the member's approval.</p></div>
      <Button onClick={onRequest} data-testid="assist-request-contact-inline" className="mt-4 rounded-full bg-emerald-800 text-white hover:bg-emerald-900"><Send className="mr-1.5 h-4 w-4" /> Request Contact</Button>
    </div>
  );
}

